
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { CalendarIcon, RotateCcw } from "lucide-react";
import { format } from "date-fns";
import { fr, enUS, es } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { CourseStatsFilters as FiltersType } from "@/hooks/useCourseStats";

const getAvailableYears = () => {
  const currentYear = new Date().getFullYear();
  const years: number[] = [];
  for (let y = currentYear; y >= 2020; y--) {
    years.push(y);
  }
  return years;
};

interface CourseStatsFiltersProps {
  filters: FiltersType;
  setFilters: (filters: FiltersType) => void;
  courses: string[];
}

const CourseStatsFilters = ({ filters, setFilters, courses }: CourseStatsFiltersProps) => {
  const { t, i18n } = useTranslation('course-stats');

  const getLocale = () => {
    switch (i18n.language) {
      case 'fr': return fr;
      case 'es': return es;
      default: return enUS;
    }
  };

  const resetFilters = () => {
    setFilters({
      holesFilter: "18",
      courseFilter: "all",
      dateFrom: null,
      dateTo: null,
      showAllRounds: true,
    });
  };

  return (
    <Card>
      <CardContent className="py-4">
        <div className="flex flex-wrap gap-4 items-end">
          {/* Holes Filter */}
          <div className="min-w-[120px]">
            <label className="text-sm font-medium mb-1 block">{t('filters.holes')}</label>
            <Select 
              value={filters.holesFilter} 
              onValueChange={(value: "all" | "9" | "18") => setFilters({ ...filters, holesFilter: value, courseFilter: "all" })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('filters.allHoles')}</SelectItem>
                <SelectItem value="9">9 {t('filters.holesLabel')}</SelectItem>
                <SelectItem value="18">18 {t('filters.holesLabel')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Course Filter */}
          <div className="min-w-[180px]">
            <label className="text-sm font-medium mb-1 block">{t('filters.course')}</label>
            <Select 
              value={filters.courseFilter} 
              onValueChange={(value) => setFilters({ ...filters, courseFilter: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('filters.allCourses')}</SelectItem>
                {courses.map((course) => (
                  <SelectItem key={course} value={course}>{course}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Year Selector */}
          <div className="min-w-[120px]">
            <label className="text-sm font-medium mb-1 block">{t('filters.year')}</label>
            <Select
              value={filters.showAllRounds ? "all" : "custom"}
              onValueChange={(value) => {
                if (value === "all") {
                  setFilters({ ...filters, showAllRounds: true });
                } else {
                  const year = parseInt(value);
                  if (!isNaN(year)) {
                    setFilters({
                      ...filters,
                      showAllRounds: false,
                      dateFrom: new Date(year, 0, 1),
                      dateTo: new Date(year, 11, 31),
                    });
                  } else {
                    setFilters({ ...filters, showAllRounds: false });
                  }
                }
              }}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('filters.allYears')}</SelectItem>
                {getAvailableYears().map((year) => (
                  <SelectItem key={year} value={String(year)}>{year}</SelectItem>
                ))}
                <SelectItem value="custom">{t('filters.customDates')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Date From */}
          <div>
            <label className="text-sm font-medium mb-1 block">{t('filters.from')}</label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  disabled={filters.showAllRounds}
                  className={cn(
                    "w-[160px] justify-start text-left font-normal",
                    !filters.dateFrom && "text-muted-foreground",
                    filters.showAllRounds && "opacity-50"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {filters.dateFrom ? format(filters.dateFrom, "P", { locale: getLocale() }) : t('filters.selectDate')}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={filters.dateFrom || undefined}
                  onSelect={(date) => setFilters({ ...filters, dateFrom: date || null, showAllRounds: false })}
                  locale={getLocale()}
                  className="pointer-events-auto"
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Date To */}
          <div>
            <label className="text-sm font-medium mb-1 block">{t('filters.to')}</label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  disabled={filters.showAllRounds}
                  className={cn(
                    "w-[160px] justify-start text-left font-normal",
                    !filters.dateTo && "text-muted-foreground",
                    filters.showAllRounds && "opacity-50"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {filters.dateTo ? format(filters.dateTo, "P", { locale: getLocale() }) : t('filters.selectDate')}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={filters.dateTo || undefined}
                  onSelect={(date) => setFilters({ ...filters, dateTo: date || null, showAllRounds: false })}
                  locale={getLocale()}
                  className="pointer-events-auto"
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Reset Button */}
          <Button variant="ghost" size="icon" onClick={resetFilters} title={t('filters.reset')}>
            <RotateCcw className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default CourseStatsFilters;
